import type { ReactNode, Ref } from "react";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Table, type TableRowProps } from "@chakra-ui/react";
import { CopyableTable } from "./tableCopy";
import {
  type ColumnDef,
  type OnChangeFn,
  type Row,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";

type RowExtras = {
  ref?: Ref<HTMLTableRowElement>;
  "data-index"?: number;
};

type DataTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  getRowId?: (row: TData, index: number) => string;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  enableSorting?: boolean;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  onRowClick?: (row: TData) => void;
  /**
   * Extra props for one row, from its data -- a dimmed row for something
   * inactive, say. Applied before the table's own, which therefore win.
   */
  getRowProps?: (row: TData) => TableRowProps;
  emptyMessage?: string;
  stickyHeader?: boolean;
  /**
   * Render nested rows. Sub-rows start expanded, because the nesting here is
   * organisational detail the reader wants in front of them -- a unit and its
   * sub-units -- rather than a drill-down they have to discover.
   */
  getSubRows?: (row: TData) => TData[] | undefined;
  size?: "sm" | "md" | "lg";
  interactive?: boolean;
  /**
   * Render only the rows visible in a scroll container instead of every row.
   * Greatly reduces the number of Chakra-styled nodes mounted at once for large
   * datasets. `true` gives the table its own vertical scroll area; "window"
   * keeps it in the page's scroll, with the header pinned to the page as
   * `stickyHeader` does -- one scrollbar rather than a box inside a page.
   */
  virtualized?: boolean | "window";
  /**
   * Fixed column widths by column id ("30%", "8rem"); columns not named share
   * what is left. Without them the browser sizes columns from the rows that
   * are drawn -- which, virtualized, change as the page scrolls, so the
   * columns jumped, and at some window widths a new width wrapped a cell,
   * changed which rows were drawn and changed the widths back, for ever.
   */
  columnWidths?: Record<string, string>;
  /**
   * Initial estimated row height in px, used by the virtualizer before real
   * rows are measured. A close value reduces the initial scrollbar adjustment.
   */
  estimatedRowHeight?: number;
  /** Max height of the scroll container when virtualized. Defaults to "70vh". */
  maxHeight?: string | number;
  /**
   * Render rows in growing batches across frames instead of all at once. The
   * first batch paints almost immediately for fast perceived load, then the
   * remaining rows fill in during idle time. Unlike virtualization, every row
   * ends up in the DOM (find-in-page, print, and a11y keep working). Ignored
   * when `virtualized` is set.
   */
  progressive?: boolean;
  /** Rows added per batch on each subsequent fill. Defaults to 30. */
  progressiveBatchSize?: number;
  /**
   * Rows rendered in the very first (synchronous) batch. Keep this small for the
   * fastest possible first paint. Defaults to `progressiveBatchSize`.
   */
  initialBatchSize?: number;
};

/**
 * Returns how many rows should currently be rendered. When `enabled`, it starts
 * at `initialBatch` and grows by `batchSize` each idle frame until all rows are
 * shown. The count resets whenever the row set changes (sort/filter/new data)
 * so a re-sort starts fast again instead of rendering everything in one frame.
 */
function useProgressiveRowCount<TData>(
  rows: Row<TData>[],
  { enabled, batchSize, initialBatch }: { enabled: boolean; batchSize: number; initialBatch: number },
): number {
  const total = rows.length;
  const [count, setCount] = useState(() => (enabled ? Math.min(initialBatch, total) : total));

  // Reset when the row set changes (sort/filter/data) or when toggling modes.
  useEffect(() => {
    setCount(enabled ? Math.min(initialBatch, total) : total);
  }, [rows, enabled, initialBatch, total]);

  // Grow the rendered window during idle time until everything is shown.
  useEffect(() => {
    if (!enabled || count >= total) return;
    const hasIdle = typeof window.requestIdleCallback === "function";
    const handle = hasIdle
      ? window.requestIdleCallback(() => setCount((c) => Math.min(c + batchSize, total)), { timeout: 100 })
      : window.requestAnimationFrame(() => setCount((c) => Math.min(c + batchSize, total)));
    return () => {
      if (hasIdle) {
        window.cancelIdleCallback(handle as number);
      } else {
        window.cancelAnimationFrame(handle as number);
      }
    };
  }, [enabled, count, total, batchSize]);

  return enabled ? count : total;
}

function DataTableInner<TData>({
  data,
  columns,
  getRowId,
  sorting,
  onSortingChange,
  enableSorting = false,
  enableRowSelection,
  rowSelection,
  onRowSelectionChange,
  onRowClick,
  getRowProps,
  emptyMessage = "No records found.",
  stickyHeader = false,
  getSubRows,
  size = "md",
  interactive = true,
  virtualized = false,
  estimatedRowHeight = 64,
  maxHeight = "70vh",
  progressive = false,
  progressiveBatchSize = 30,
  initialBatchSize,
  columnWidths,
}: DataTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: {
      ...(sorting !== undefined ? { sorting } : {}),
      ...(rowSelection !== undefined ? { rowSelection } : {}),
    },
    // Spread only when set. TanStack supplies its own onSortingChange by
    // default -- makeStateUpdater('sorting'), which drives its internal state --
    // and setSorting no-ops when the option is null. Passing the key explicitly
    // as undefined overwrites that default, so every uncontrolled table had a
    // sortable-looking header that did nothing when clicked.
    ...(onSortingChange ? { onSortingChange } : {}),
    ...(onRowSelectionChange ? { onRowSelectionChange } : {}),
    ...(enableRowSelection !== undefined ? { enableRowSelection } : {}),
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting ? { getSortedRowModel: getSortedRowModel() } : {}),
    ...(getSubRows
      ? { getSubRows, getExpandedRowModel: getExpandedRowModel(), initialState: { expanded: true } }
      : {}),
  });

  const rows = table.getRowModel().rows;

  // Progressive rendering is mutually exclusive with virtualization. The hook is
  // called unconditionally (it no-ops and returns the full count when disabled)
  // to keep hook order stable across the `virtualized` early return below.
  const progressiveCount = useProgressiveRowCount(rows, {
    enabled: progressive && !virtualized,
    batchSize: progressiveBatchSize,
    initialBatch: initialBatchSize ?? progressiveBatchSize,
  });
  const visibleRows = progressive && !virtualized ? rows.slice(0, progressiveCount) : rows;

  const renderRow = (row: Row<TData>, extras?: RowExtras) => (
    <Table.Row
      {...getRowProps?.(row.original)}
      key={row.id}
      data-selected={row.getIsSelected() ? "" : undefined}
      cursor={onRowClick ? "pointer" : undefined}
      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
      {...extras}
    >
      {row.getVisibleCells().map((cell) => (
        <Table.Cell key={cell.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </Table.Cell>
      ))}
    </Table.Row>
  );

  const header = (
    // Chakra's stickyHeader sticks the row to its nearest SCROLL container. The
    // tables here render in full inside the page, so there is no such container
    // and nothing sticks. Positioning the header cells against the document
    // scroll is what actually pins them -- which also means no ancestor may
    // have overflow set, or it becomes the containing block and clips them.
    //
    // The background is required: without it the rows scroll visibly through
    // the header.
    <Table.Header
      css={
        stickyHeader
          ? {
              "& th": {
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: "var(--chakra-colors-bg)",
                // A sticky row sits over the body, so it needs its own bottom
                // edge: the table's own border scrolls away with the rows.
                // An inset shadow, not a border: with border-collapse the
                // header's own border belongs to the collapsed grid and scrolls
                // away with the rows, leaving the pinned row edgeless.
                boxShadow: "inset 0 -1px 0 var(--chakra-colors-border)",
              },
            }
          : undefined
      }
    >
      {table.getHeaderGroups().map((headerGroup) => (
        <Table.Row key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const canSort = enableSorting && header.column.getCanSort();
            const sortDir = header.column.getIsSorted();
            return (
              <Table.ColumnHeader
                key={header.id}
                colSpan={header.colSpan}
                w={columnWidths?.[header.column.id]}
                cursor={canSort ? "pointer" : undefined}
                userSelect={canSort ? "none" : undefined}
                onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
                {sortDir ? (sortDir === "asc" ? " ↑" : " ↓") : null}
              </Table.ColumnHeader>
            );
          })}
        </Table.Row>
      ))}
    </Table.Header>
  );

  if (virtualized === "window") {
    return <WindowVirtualizedTable
      fixedLayout={columnWidths !== undefined}
      rows={rows}
      header={header}
      renderRow={renderRow}
      columnCount={columns.length}
      emptyMessage={emptyMessage}
      size={size}
      interactive={interactive}
      estimatedRowHeight={estimatedRowHeight}
      maxHeight={maxHeight}
    />;
  }

  if (virtualized) {
    return <VirtualizedTable
      rows={rows}
      header={header}
      renderRow={renderRow}
      columnCount={columns.length}
      emptyMessage={emptyMessage}
      size={size}
      interactive={interactive}
      estimatedRowHeight={estimatedRowHeight}
      maxHeight={maxHeight}
    />;
  }

  return (
    <CopyableTable>
      <Table.Root size={size} interactive={interactive}>
        {header}
        <Table.Body>
          {rows.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={columns.length} color="fg.muted">
                {emptyMessage}
              </Table.Cell>
            </Table.Row>
          ) : (
            visibleRows.map((row) => renderRow(row))
          )}
        </Table.Body>
      </Table.Root>
    </CopyableTable>
  );
}

type VirtualizedTableProps<TData> = {
  rows: Row<TData>[];
  header: ReactNode;
  renderRow: (row: Row<TData>, extras?: RowExtras) => ReactNode;
  columnCount: number;
  emptyMessage: string;
  size: "sm" | "md" | "lg";
  interactive: boolean;
  estimatedRowHeight: number;
  maxHeight: string | number;
};

function VirtualizedTable<TData>({
  rows,
  header,
  renderRow,
  columnCount,
  emptyMessage,
  size,
  interactive,
  estimatedRowHeight,
  maxHeight,
}: VirtualizedTableProps<TData>) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: 8,
    // Measure each row's real height so variable-height rows don't cause the
    // scrollbar to jump as they enter the viewport.
    measureElement: (element) => element?.getBoundingClientRect().height ?? estimatedRowHeight,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const firstVirtualRow = virtualRows[0];
  const lastVirtualRow = virtualRows[virtualRows.length - 1];
  const paddingTop = firstVirtualRow ? firstVirtualRow.start : 0;
  const paddingBottom = lastVirtualRow ? totalSize - lastVirtualRow.end : 0;

  return (
    <Box ref={scrollRef} overflowY="auto" overflowX="auto" maxH={maxHeight}>
      {/* Virtualized tables copy only what is mounted, which is what is on
          screen -- selecting past the viewport cannot include rows the browser
          has not rendered either way. */}
      <CopyableTable>
      <Table.Root size={size} interactive={interactive} stickyHeader>
        {header}
        <Table.Body>
          {rows.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={columnCount} color="fg.muted">
                {emptyMessage}
              </Table.Cell>
            </Table.Row>
          ) : (
            <>
              {paddingTop > 0 ? (
                <Table.Row aria-hidden>
                  <Table.Cell colSpan={columnCount} p={0} border="none" height={`${paddingTop}px`} />
                </Table.Row>
              ) : null}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                if (!row) {
                  return null;
                }
                return renderRow(row, {
                  ref: virtualizer.measureElement,
                  "data-index": virtualRow.index,
                });
              })}
              {paddingBottom > 0 ? (
                <Table.Row aria-hidden>
                  <Table.Cell colSpan={columnCount} p={0} border="none" height={`${paddingBottom}px`} />
                </Table.Row>
              ) : null}
            </>
          )}
        </Table.Body>
      </Table.Root>
      </CopyableTable>
    </Box>
  );
}

/**
 * Virtualized against the page's own scroll rather than a box of its own.
 *
 * The virtualizer needs to know where the table starts on the page
 * (`scrollMargin`), measured after layout and again whenever the page above
 * it changes height -- a filter row wrapping, a notice appearing.
 */
function WindowVirtualizedTable<TData>({
  fixedLayout,
  rows,
  header,
  renderRow,
  columnCount,
  emptyMessage,
  size,
  interactive,
  estimatedRowHeight,
}: VirtualizedTableProps<TData> & { fixedLayout: boolean }) {
  const tableRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const element = tableRef.current;
    if (!element) {
      return;
    }
    const measure = () => setScrollMargin(element.getBoundingClientRect().top + window.scrollY);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => estimatedRowHeight,
    overscan: 8,
    scrollMargin,
    measureElement: (element) => element?.getBoundingClientRect().height ?? estimatedRowHeight,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const firstVirtualRow = virtualRows[0];
  const lastVirtualRow = virtualRows[virtualRows.length - 1];
  // Item positions include the margin; the total does not.
  const paddingTop = firstVirtualRow ? firstVirtualRow.start - scrollMargin : 0;
  const paddingBottom = lastVirtualRow ? totalSize - (lastVirtualRow.end - scrollMargin) : 0;

  return (
    <Box ref={tableRef}>
      <CopyableTable>
      <Table.Root size={size} interactive={interactive} tableLayout={fixedLayout ? "fixed" : undefined}>
        {header}
        <Table.Body>
          {rows.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={columnCount} color="fg.muted">
                {emptyMessage}
              </Table.Cell>
            </Table.Row>
          ) : (
            <>
              {paddingTop > 0 ? (
                <Table.Row aria-hidden>
                  <Table.Cell colSpan={columnCount} p={0} border="none" height={`${paddingTop}px`} />
                </Table.Row>
              ) : null}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                if (!row) {
                  return null;
                }
                return renderRow(row, {
                  ref: virtualizer.measureElement,
                  "data-index": virtualRow.index,
                });
              })}
              {paddingBottom > 0 ? (
                <Table.Row aria-hidden>
                  <Table.Cell colSpan={columnCount} p={0} border="none" height={`${paddingBottom}px`} />
                </Table.Row>
              ) : null}
            </>
          )}
        </Table.Body>
      </Table.Root>
      </CopyableTable>
    </Box>
  );
}

/**
 * The table, memoised.
 *
 * It is the most expensive thing on every page that has one, and most of what
 * re-renders those pages does not touch it: opening a drawer, toggling a
 * dialog, a toast arriving. Comparing props is far cheaper than rebuilding a
 * few hundred rows.
 *
 * The cast keeps the generic. `memo` erases type parameters, and a table that
 * cannot infer its row type would push `any` into every column definition --
 * a worse trade than the cast, which is checked against the function it wraps.
 */
export const DataTable = memo(DataTableInner) as typeof DataTableInner;
